import React, { useMemo } from "react";
import moment from "moment";
import {
  useApiEventsQuery,
  useApiMasterScheduleEventsQuery,
} from "@src/queries/scheduleQueries";
import { IScheduleEvent } from "@src/types/schedules";
import { useAuthCurator } from "@src/hooks/useAuth";
import { FEATURES } from "@src/constants";
import TableComponent from "@src/components/TableComponent";
import WrapperContainer from "@components/WrapperContainer";
import ContentContainer from "@src/components/ContentContainer";
import PageTopSection from "@src/components/PageTopSection";
import PageTitle from "@src/components/PageTitle";
import Tooltip from "@src/components/Tooltip";
import CalendarIcon from "@assets/img/calendar-outline.svg";
import {
  ScheduleOverviewHeader,
  ScheduleOverviewHeaderLeft,
  ScheduleOverviewHeaderCenter,
  ButtonsContainer,
  ToggleViewButton,
} from "./ScheduleCalendar.styles";
import { useSettings } from "@src/context/SettingsContext";
import Spinner from "@src/components/Spinner";
import RenderContentStatus from "@src/components/RenderContentStatus";
import { theme } from "@src/theme";

type CuratorShowRow = {
  id: string;
  date: string;
  time: string;
  showTitle: string;
  playlistTitle: string;
  isLive: boolean;
};

const CuratorScheduleView = ({
  onShowFullCalendar,
}: {
  onShowFullCalendar: () => void;
}): React.JSX.Element => {
  const curator = useAuthCurator();
  const {
    tableSettings: { playlistsTable: compactUI },
  } = useSettings();

  const getRangeForWeeks = (): { start: Date; end: Date } => {
    const startOfRange = moment().startOf("isoWeek");
    const endOfRange = startOfRange.clone().add(7, "days");
    return { start: startOfRange.toDate(), end: endOfRange.toDate() };
  };
  const range = getRangeForWeeks();

  const globalRange = useMemo(
    () => ({
      start: moment().startOf("day").toDate(),
      end: moment()
        .add(FEATURES.SCHEDULE_CALENDAR_WEEKS, "weeks")
        .endOf("day")
        .toDate(),
    }),
    []
  );

  const { data: globalEventsResponse, isLoading: globalEventsLoading } =
    useApiEventsQuery(globalRange.start, globalRange.end);

  const { data: masterEventsResponse, isLoading: masterEventsLoading } =
    useApiMasterScheduleEventsQuery(range.start, range.end);

  const masterEvents = useMemo(() => {
    return masterEventsResponse?.data?.data || [];
  }, [masterEventsResponse]);

  const upcomingShows = useMemo((): CuratorShowRow[] => {
    if (!masterEvents || !curator.id) return [];

    const curatorMasterEvents = masterEvents.filter(
      (master) => master.curator_id === curator.id
    );

    const eventsFetched = globalEventsResponse?.data?.data || [];
    const curatorScheduledEvents = eventsFetched.filter(
      (e: IScheduleEvent) => e.playlist?.curator?.name === curator.name
    );

    const allDates = [
      ...curatorMasterEvents.flatMap((master) => {
        const entries: {
          date: string;
          title: string;
          playlistTitle?: string;
        }[] = [];
        if (master.next_schedule) {
          entries.push({
            date: master.next_schedule,
            title: master.title || "",
          });
        }
        const upcomingStr = master.upcoming_schedules || "";
        upcomingStr
          .split("|")
          .filter((d) => !!d)
          .forEach((d) => entries.push({ date: d, title: master.title || "" }));
        return entries;
      }),
      ...curatorScheduledEvents.map((e) => ({
        date: e.startTime,
        title: e.show?.title || "",
        playlistTitle: e.playlist?.title,
      })),
    ];

    const scheduledByHour = new Map<string, string>();
    curatorScheduledEvents.forEach((e: IScheduleEvent) => {
      const startKey = moment(e.startTime).startOf("hour").toISOString();
      if (e.playlist?.title) {
        scheduledByHour.set(startKey, e.playlist.title);
      }
    });

    return allDates
      .map((entry) => ({ m: moment(entry.date), ...entry }))
      .filter(
        ({ m }) => m.isValid() && m.clone().add(2, "hours").isAfter(moment())
      )
      .sort((a, b) => a.m.diff(b.m))
      .filter(
        ({ m }, index, arr) =>
          index === 0 || m.diff(arr[index - 1].m, "minutes") > 30
      )
      .map(({ m, title, playlistTitle }) => {
        const isLive =
          m.isBefore(moment()) && m.clone().add(2, "hours").isAfter(moment());
        const hourKey = m.clone().startOf("hour").toISOString();
        const finalPlaylistTitle =
          playlistTitle || scheduledByHour.get(hourKey) || "";

        return {
          id: m.toISOString(),
          date: m.format("MM/DD/YY"),
          time: `${m.format("HH:mm")} - ${m.clone().add(2, "hours").format("HH:mm")}`,
          showTitle: title,
          playlistTitle: finalPlaylistTitle,
          isLive,
        };
      });
  }, [masterEvents, curator.id, curator.name, globalEventsResponse]);

  const columns = useMemo(
    () => [
      {
        Header: "Date",
        accessor: "date",
      },
      {
        Header: "Time",
        accessor: "time",
        Cell: ({
          row,
          value,
        }: {
          row: CuratorShowRow;
          value: string;
        }): React.JSX.Element => (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {value}
            {row.isLive && (
              <span
                style={{
                  color: theme.colors.red,
                  fontSize: "12px",
                  fontWeight: "bold",
                }}
              >
                LIVE NOW
              </span>
            )}
          </div>
        ),
      },
      {
        Header: "Show",
        accessor: "showTitle",
      },
      {
        Header: "Scheduled Playlist",
        accessor: "playlistTitle",
        Cell: ({ value }: { value: string }): React.JSX.Element => {
          if (!value) {
            return (
              <span style={{ color: theme.colors.red, fontWeight: "bold" }}>
                MISSING PLAYLIST
              </span>
            );
          }
          return <span>{value}</span>;
        },
      },
    ],
    []
  );

  return (
    <WrapperContainer noScroll>
      <PageTopSection>
        <ScheduleOverviewHeader>
          <ScheduleOverviewHeaderLeft>
            <PageTitle>Schedule</PageTitle>
            <ButtonsContainer>
              <Tooltip content="Full Calendar View" position="bottom">
                <ToggleViewButton onClick={onShowFullCalendar}>
                  <img src={CalendarIcon} alt="calendar" /> Calendar
                </ToggleViewButton>
              </Tooltip>
            </ButtonsContainer>
          </ScheduleOverviewHeaderLeft>
          <ScheduleOverviewHeaderCenter
            aria-busy={globalEventsLoading || masterEventsLoading}
            $active={globalEventsLoading || masterEventsLoading}
          >
            <Spinner small inline />
          </ScheduleOverviewHeaderCenter>
        </ScheduleOverviewHeader>
      </PageTopSection>
      <ContentContainer allowScroll>
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <TableComponent
            columns={columns}
            data={upcomingShows}
            $compactUI={compactUI}
          />
          <RenderContentStatus
            loading={globalEventsLoading || masterEventsLoading}
            noResultsFoundMessage={
              !(globalEventsLoading || masterEventsLoading) &&
              upcomingShows.length === 0
                ? "You have no scheduled shows"
                : ""
            }
            results={upcomingShows.length}
          />
        </div>
      </ContentContainer>
    </WrapperContainer>
  );
};

export default CuratorScheduleView;
